import { HsIndexedGeometryData, hsGenerateCubeGeometryData, type HsRegularGeometryData } from '@sibe/hyperscene';

/**
 * Cube geometry data with texture coordinates on every face. `shareVertices` false keeps the 36 corners of the
 * 12 triangles apart, like a non-indexed mesh; true merges equal corners into 24 vertices.
 */
export function makeTexturedCubeGeometryData(shareVertices: boolean): HsIndexedGeometryData {
  const cube: HsRegularGeometryData = hsGenerateCubeGeometryData();
  const vertices: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const vertexIndices: Map<string, number> = new Map<string, number>();
  for (let i: number = 0; i < cube.vertices.length / 3; i++) {
    const position: number[] = [cube.vertices[i * 3], cube.vertices[i * 3 + 1], cube.vertices[i * 3 + 2]];
    const normal: number[] = [cube.normals[i * 3], cube.normals[i * 3 + 1], cube.normals[i * 3 + 2]];
    // Projects the corner onto its face plane: the two axes other than the normal axis become u and v.
    const normalAxis: number = normal.findIndex((value: number) => value !== 0);
    const [uAxis, vAxis]: number[] = [0, 1, 2].filter((axis: number) => axis !== normalAxis);
    const uv: number[] = [(position[uAxis] + 1) / 2, (position[vAxis] + 1) / 2];
    const key: string = [...position, ...normal].join(',');
    const existingIndex: number | undefined = shareVertices ? vertexIndices.get(key) : undefined;
    if (existingIndex !== undefined) {
      indices.push(existingIndex);
      continue;
    }
    const index: number = vertices.length / 3;
    vertexIndices.set(key, index);
    vertices.push(...position);
    normals.push(...normal);
    uvs.push(...uv);
    indices.push(index);
  }
  return new HsIndexedGeometryData(vertices, normals, indices, uvs);
}
